/**
 * `/panel` — the one entry point for editors.
 *
 * Signed out: a sign-in form for the Strapi account (the gateway checks it and
 * sets an HttpOnly session cookie). Signed in: who you are, your roles, the CMS
 * link and how draft preview works. `?next=/podglad/…` sends the editor back to
 * the page that asked them to sign in.
 *
 * With VITE_PANEL_URL set (production) the panel lives on its own host
 * (panel.<domain>) and this route only sends the browser there. Its session
 * cookie is scoped to the site's domain, so a sign-in there also opens the
 * draft preview here; signing in again on this page is not needed.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import { EDITOR_ROLES } from "../core/deps.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";

/** Only same-origin paths are followed after sign-in — never `//host` or a URL. */
export function safeNext(next: string | undefined): string | undefined {
	if (!next || !next.startsWith("/") || next.startsWith("//") || next.includes("\\"))
		return undefined;
	return next;
}

export function panelView(ctx: AppContext, match: RouteMatch): HTMLElement {
	const copy = ctx.copy().panel;
	applyPageSeo(ctx, { title: copy.title, description: "", noindex: true });

	if (ctx.panelUrl) {
		window.location.replace(ctx.panelUrl);
		return el(
			"div",
			{ class: "mb-view mb-view--panel" },
			el(
				"section",
				{ class: "mb-section mb-section--white" },
				el(
					"div",
					{ class: "mb-section__inner mb-panel" },
					el("h1", { class: "mb-pagehead__title" }, copy.title),
					el("p", {}, el("a", { href: ctx.panelUrl }, ctx.panelUrl)),
				),
			),
		);
	}

	const body = ctx.auth.isAuthenticated()
		? signedIn(ctx)
		: signInForm(ctx, safeNext(match.query.next));

	return el(
		"div",
		{ class: "mb-view mb-view--panel" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner mb-panel" },
				el("h1", { class: "mb-pagehead__title" }, copy.title),
				body,
			),
		),
	);
}

function signInForm(ctx: AppContext, next: string | undefined): HTMLElement {
	const copy = ctx.copy().panel;
	const email = el("input", {
		id: "mb-panel-email",
		class: "mb-field__input",
		type: "email",
		name: "email",
		autocomplete: "username",
		required: true,
	});
	const password = el("input", {
		id: "mb-panel-password",
		class: "mb-field__input",
		type: "password",
		name: "password",
		autocomplete: "current-password",
		required: true,
	});
	const error = el("p", { class: "mb-field__error", role: "alert", hidden: true });
	const submit = el(
		"button",
		{ class: "mb-cta mb-cta--accent mb-form__submit", type: "submit" },
		copy.submit,
	);

	const form = el(
		"form",
		{
			class: "mb-formcard mb-panel__form",
			novalidate: true,
			onSubmit: (event: Event) => {
				event.preventDefault();
				if (!email.value.trim() || !password.value) {
					error.textContent = copy.errorInvalid;
					error.hidden = false;
					return;
				}
				submit.disabled = true;
				submit.textContent = copy.submitting;
				error.hidden = true;
				void ctx.auth.signIn(email.value.trim(), password.value).then((result) => {
					if (result === "ok") {
						ctx.track({ type: "editor_sign_in" });
						ctx.router.go(next ?? ctx.href("panel"));
						return;
					}
					password.value = "";
					submit.disabled = false;
					submit.textContent = copy.submit;
					error.textContent =
						result === "invalid"
							? copy.errorInvalid
							: result === "rate_limited"
								? copy.errorRateLimited
								: copy.errorUnavailable;
					error.hidden = false;
				});
			},
		},
		el("p", { class: "mb-pagehead__lead" }, copy.signInLead),
		el(
			"div",
			{ class: "mb-field" },
			el("label", { class: "mb-field__label", for: "mb-panel-email" }, copy.email),
			email,
		),
		el(
			"div",
			{ class: "mb-field" },
			el("label", { class: "mb-field__label", for: "mb-panel-password" }, copy.password),
			password,
		),
		error,
		submit,
	);
	return form;
}

function signedIn(ctx: AppContext): HTMLElement {
	const copy = ctx.copy().panel;
	const user = ctx.auth.getUser();
	const roles = ctx.auth.getRoles();
	const isEditor = roles.some((r) => (EDITOR_ROLES as readonly string[]).includes(r));

	const signOut = el(
		"button",
		{
			class: "mb-cta mb-cta--outline",
			type: "button",
			onClick: () => {
				void ctx.auth.signOut().finally(() => ctx.router.go(ctx.href("panel")));
			},
		},
		copy.signOut,
	);

	return el(
		"div",
		{ class: "mb-panel__body" },
		el(
			"p",
			{ class: "mb-panel__user" },
			`${copy.signedInAs} `,
			el("strong", null, user?.name || user?.email || ""),
		),
		el("h2", { class: "mb-panel__heading" }, copy.rolesTitle),
		el(
			"ul",
			{ class: "mb-panel__roles" },
			...roles.map((r) => el("li", { class: "mb-pill mb-pill--neutral" }, copy.roleNames[r] ?? r)),
		),
		isEditor ? null : el("p", { class: "mb-panel__note" }, copy.noEditorRole),
		ctx.cmsUrl
			? el(
					"div",
					{ class: "mb-panel__card" },
					el("h2", { class: "mb-panel__heading" }, copy.cmsTitle),
					el("p", null, copy.cmsDesc),
					el(
						"a",
						{ class: "mb-cta mb-cta--accent", href: ctx.cmsUrl, rel: "external noopener" },
						copy.cmsCta,
					),
				)
			: null,
		isEditor
			? el(
					"div",
					{ class: "mb-panel__card" },
					el("h2", { class: "mb-panel__heading" }, copy.previewTitle),
					el("p", null, copy.previewDesc),
				)
			: null,
		signOut,
	);
}
